import React, { useEffect, useMemo, useState } from "react";
import { ScrollView, View, StyleSheet, Pressable, TextInput } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { roleService } from "@/services/roleService";
import { RoleType } from "@/types/db/roles";
import useAuth from "@/hooks/useAuth";
import useToast from "@/hooks/useToast";
import { useModal } from "@/hooks/useModal";
import { ConfirmModal } from "@/components/Modals/ConfirmModal";
import { getEntityPermissions } from "@/lib/permissions";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useTheme } from "@/hooks/useTheme";
import { router } from "expo-router";

export default function RolesSettingsPage() {
  const { userRoles, loading: authLoading } = useAuth();
  const { colors } = useTheme();
  const toast = useToast();
  const { openModal, closeModal } = useModal();
  const [roles, setRoles] = useState<RoleType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);

  // Filter roles by name and description
  const filteredRoles = useMemo(() => {
    if (!searchValue.trim()) return roles;
    const search = searchValue.toLowerCase();
    return roles.filter(
      (role) =>
        (role.name as string)?.toLowerCase().includes(search) ||
        (role.description as string)?.toLowerCase().includes(search),
    );
  }, [roles, searchValue]);

  // Get permissions for roles
  const permissions = getEntityPermissions(userRoles, "roles");

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const data = await roleService.getRoles();
      setRoles(data ?? []);
    } catch (error: any) {
      toast.showToast({
        title: "Failed to load roles",
        message: error?.message || "Unable to fetch roles",
        toastType: "error",
      });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    // Don't fetch if auth is still loading
    if (authLoading) return;

    if (permissions.read) {
      fetchRoles();
    } else {
      // If no read permission, stop loading
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions.read, authLoading]);

  const handleDeleteRole = (roleId: string, roleName: string) => {
    openModal(
      <ConfirmModal
        title="Delete Role"
        message={`Are you sure you want to delete "${roleName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={async () => {
          closeModal();
          try {
            await roleService.deleteRolesByIds([roleId]);
            setRoles(roles.filter((r) => r.id !== roleId));

            toast.showToast({
              title: "Role deleted",
              message: "Role deleted successfully",
              toastType: "success",
            });
          } catch (error: any) {
            toast.showToast({
              title: "Failed to delete role",
              message: error?.message || "Unable to delete role",
              toastType: "error",
            });
          }
        }}
        onCancel={closeModal}
      />,
    );
  };

  // If no read permission, show error
  if (!permissions.read) {
    return (
      <ScrollView contentContainerStyle={styles.centered}>
        <MaterialCommunityIcons name="lock" size={48} color={colors.textSecondary} />
        <ThemedText type="defaultSemiBold" style={{ marginTop: 16 }}>
          Access Denied
        </ThemedText>
        <ThemedText style={{ opacity: 0.7, marginTop: 8 }}>You don&apos;t have permission to view roles</ThemedText>
      </ScrollView>
    );
  }

  if (authLoading || loading) {
    return (
      <ScrollView contentContainerStyle={styles.centered}>
        <ThemedText>{authLoading ? "Loading authentication..." : "Loading roles..."}</ThemedText>
      </ScrollView>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerTop}>
          <View>
            <ThemedText type="title">Roles Management</ThemedText>
            <ThemedText style={{ opacity: 0.7, marginTop: 4 }}>Manage user roles and permissions</ThemedText>
          </View>

          {permissions.create && (
            <Button
              text="New Role"
              onPress={() => router.push("/settings/roles/new")}
              style={[styles.addButton, { backgroundColor: colors.green.background }]}
              textStyle={{ color: "white" }}
            />
          )}
        </View>

        <View style={styles.searchRow}>
          <View style={[styles.searchContainer, { borderBottomColor: searchFocused ? colors.primary : colors.border }]}>
            <MaterialCommunityIcons
              name="magnify"
              size={20}
              color={searchFocused ? colors.primary : colors.textSecondary}
            />
            <TextInput
              value={searchValue}
              onChangeText={setSearchValue}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              placeholder="Search..."
              placeholderTextColor={colors.textSecondary}
              style={[styles.searchInput, { color: colors.text, outlineStyle: "none" } as any]}
            />
            {searchValue.length > 0 && (
              <Pressable onPress={() => setSearchValue("")}>
                <MaterialCommunityIcons name="close-circle" size={18} color={colors.textSecondary} />
              </Pressable>
            )}
          </View>
        </View>
      </View>

      {/* Roles List */}
      <ScrollView style={styles.scrollView}>
        {filteredRoles.length === 0 ? (
          <View style={styles.emptyState}>
            <MaterialCommunityIcons name="account-group-outline" size={64} color={colors.textSecondary} />
            <ThemedText style={{ marginTop: 16, opacity: 0.7 }}>
              {searchValue ? "No roles match your search" : "No roles configured"}
            </ThemedText>
            {!searchValue && permissions.create && (
              <Button
                text="Create First Role"
                onPress={() => router.push("/settings/roles/new")}
                style={{ marginTop: 16 }}
              />
            )}
          </View>
        ) : (
          <View style={styles.rolesList}>
            {filteredRoles.map((role) => (
              <Pressable
                key={role.id as string}
                style={[
                  styles.roleCard,
                  {
                    backgroundColor: colors.card,
                    borderColor: colors.border,
                  },
                ]}
                onPress={() => router.push(`/settings/roles/${role.id}`)}
              >
                <View style={styles.roleHeader}>
                  <View style={styles.roleInfo}>
                    <View style={styles.titleRow}>
                      <ThemedText type="defaultSemiBold" style={{ fontSize: 16 }}>
                        {role.name as string}
                      </ThemedText>
                      {Object.entries(role.meta_data?.permissions || {})
                        .filter(([_, value]) => {
                          if (typeof value === "boolean") return value;
                          if (typeof value === "object") {
                            return Object.values(value).some((v) => v === true);
                          }
                          return false;
                        })
                        .slice(0, 5)
                        .map(([key]) => (
                          <View key={key} style={[styles.chip, { backgroundColor: colors.primary + "20" }]}>
                            <ThemedText style={{ fontSize: 11, color: colors.primary }}>{key}</ThemedText>
                          </View>
                        ))}
                      {Object.keys(role.meta_data?.permissions || {}).length > 5 && (
                        <View style={[styles.chip, { backgroundColor: colors.textSecondary + "20" }]}>
                          <ThemedText style={{ fontSize: 11, opacity: 0.6 }}>
                            +{Object.keys(role.meta_data?.permissions || {}).length - 5} more
                          </ThemedText>
                        </View>
                      )}
                    </View>
                    {role.description && (
                      <ThemedText style={[styles.description, { color: colors.textSecondary }]}>
                        {role.description as string}
                      </ThemedText>
                    )}
                    <ThemedText style={{ opacity: 0.6, marginTop: 4 }}>
                      {role.roles_users?.length || 0} users
                      {role.meta_data?.accept_shifts && ` · Priority: ${role.meta_data?.priority ?? 1}`}
                    </ThemedText>
                  </View>
                  {permissions.delete && (
                    <Pressable
                      style={styles.menuButton}
                      onPress={() => handleDeleteRole(role.id as string, role.name as string)}
                    >
                      <MaterialCommunityIcons name="delete" size={20} color={colors.red?.text || colors.text} />
                    </Pressable>
                  )}
                </View>
              </Pressable>
            ))}
          </View>
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centered: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  header: {
    gap: 12,
    paddingBottom: 12,
  },
  headerTop: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
  },
  searchRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
  },
  addButton: {
    paddingHorizontal: 8,
    height: 35,
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 6,
    borderBottomWidth: 1,
    width: 200,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    padding: 0,
  },
  scrollView: {
    flex: 1,
    // padding: 16,
  },
  emptyState: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 48,
  },
  rolesList: {
    gap: 12,
  },
  roleCard: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  roleHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  roleInfo: {
    flex: 1,
  },
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
  },
  description: {
    fontSize: 13,
    marginTop: 4,
  },
  menuButton: {
    padding: 4,
  },
  menuItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    padding: 12,
  },
  deleteItem: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: "#00000020",
  },
  chip: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
});
